/* NutriFitt × Symbiote — Deadly Nightshade product experience */
(() => {
  const html = document.documentElement;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const narrow = matchMedia('(max-width: 900px)');
  const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

  function money(cents, fmt) {
    const v = (Number(cents) || 0) / 100;
    const f = (n, d, t, c) => { const [i, dec] = n.toFixed(d).split('.'); return i.replace(/\B(?=(\d{3})+(?!\d))/g, t) + (dec ? c + dec : ''); };
    const out = (fmt || '${{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (m, k) => {
      if (k === 'amount_no_decimals') return f(v, 0, ',', '.');
      if (k === 'amount_with_comma_separator') return f(v, 2, '.', ',');
      if (k === 'amount_no_decimals_with_comma_separator') return f(v, 0, '.', ',');
      return f(v, 2, ',', '.');
    });
    const t = document.createElement('textarea'); t.innerHTML = out; return t.value;
  }

  /* ── title: extrusion that leans away from the pointer ── */
  function titleLight(root) {
    const lines = root.querySelectorAll('.dn-title__line');
    if (!lines.length) return;
    const cols = ['#ff2fa0', '#e81c92', '#c9137f', '#a90e6b', '#8a0b58', '#6b0845', '#4f0633', '#350422', '#200214'];
    let raf = 0, tx = .55, ty = .75, x = tx, y = ty;
    function paint() {
      x += (tx - x) * .12; y += (ty - y) * .12;
      const len = Math.hypot(x, y) || 1; let dx = x / len, dy = y / len;
      if (dy < .5) { dy = .5; dx = Math.sign(dx || 1) * .866; } /* the extrusion always falls: the melt hangs under it */
      root.__dnEx = [dx * 9, dy * 9];
      const s = cols.map((c, i) => `${(dx * (i + 1)).toFixed(2)}px ${(dy * (i + 1)).toFixed(2)}px 0 ${c}`).join(',') + `,${(dx * 14).toFixed(1)}px ${(dy * 16).toFixed(1)}px 28px rgba(0,0,0,.75)`;
      lines.forEach(l => l.style.setProperty('--dn-ex', s));
      if (Math.abs(tx - x) + Math.abs(ty - y) > .002) raf = requestAnimationFrame(paint); else raf = 0;
    }
    paint();
    if (reduced.matches) return;
    const title = root.querySelector('.dn-title');
    addEventListener('pointermove', e => {
      const r = title.getBoundingClientRect();
      if (r.bottom < -100 || r.top > innerHeight + 100) return;
      tx = clamp((r.left + r.width / 2 - e.clientX) / 300, -1, 1);
      ty = clamp((r.top + r.height / 2 - e.clientY) / 300, -1, 1);
      if (Math.abs(tx) + Math.abs(ty) < .25) { tx += .3; ty += .4; }
      if (!raf) raf = requestAnimationFrame(paint);
    }, { passive: true });
  }

  /* ── the title melts: the underside of the extruded letters softens into the same magenta, drips grow out of it,
     lumps of goo slide down each strand and feed the swelling tip (like the goo runs in the 3D world) ── */
  function titleMelt(root) {
    const title = root.querySelector('.dn-title');
    const line = title && title.querySelector('.dn-title__line--b');
    if (!line || !line.dataset.text) return;
    const cv = document.createElement('canvas');
    cv.className = 'dn-melt'; cv.setAttribute('aria-hidden', 'true');
    let gl = null;
    try { gl = cv.getContext('webgl', { alpha: true, premultipliedAlpha: true, antialias: false, powerPreference: 'low-power' }); } catch (e) {}
    if (!gl) return;
    const MAXD = 8;
    const VS = 'attribute vec2 a;void main(){gl_Position=vec4(a,0.,1.);}';
    const FS = `precision highp float;
uniform vec2 uRes;uniform sampler2D uLip;uniform float uN,uW,uK,uY0,uZ;uniform vec2 uL;
uniform vec4 uA[${MAXD}];uniform vec4 uB[${MAXD}];uniform vec4 uC[${MAXD}];
float smin(float a,float b,float k){float h=clamp(.5+.5*(b-a)/k,0.,1.);return mix(b,a,h)-k*h*(1.-h);}
float cone(vec2 p,float r1,float r2,float h){p.x=abs(p.x);float b=(r1-r2)/h;float a=sqrt(max(1.-b*b,.0001));float k=dot(p,vec2(-b,a));
 if(k<0.)return length(p)-r1;if(k>a*h)return length(p-vec2(0.,h))-r2;return dot(p,vec2(a,b))-r1;}
float scene(vec2 p){
 float d=(texture2D(uLip,p/uRes).r*255.-128.)*.25;
 for(int i=0;i<${MAXD};i++){if(float(i)>=uN)break;
  vec4 A=uA[i];vec4 B=uB[i];vec4 C=uC[i];
  vec2 q=vec2(p.x-A.x,p.y-A.y);
  if(abs(q.x)>B.x+B.y+uK*3.||q.y<-uK*3.||q.y>A.z+B.y*2.+uK*2.)continue;
  float L=max(A.z,.5);float fh=max(min(B.w,L-.5),.5);
  float dd=cone(q,B.x,A.w,fh);
  if(L>fh+.5)dd=smin(dd,cone(q-vec2(0.,fh),A.w,B.z,L-fh),uK*.5);
  dd=smin(dd,length((q-vec2(0.,L))*vec2(1.,.92))-B.y,uK*.75);
  if(C.z>0.)dd=smin(dd,length(q-vec2(0.,C.y))-C.z,uK*.6);
  d=smin(d,dd,uK);
 }
 return d;}
void main(){
 vec2 p=vec2(gl_FragCoord.x,uRes.y-gl_FragCoord.y);
 float d=scene(p);
 if(d>1.5){gl_FragColor=vec4(0.);return;}
 float e=.75;vec2 g=vec2(scene(p+vec2(e,0.))-scene(p-vec2(e,0.)),scene(p+vec2(0.,e))-scene(p-vec2(0.,e)));
 float gn=length(g);vec2 gd=gn>1e-4?g/gn:vec2(0.,1.);
 float t=clamp(-d/uW,0.,1.);float edge=1.-t;
 /* a round tube: height from the distance to the edge */
 float hh=sqrt(max(1.-edge*edge,0.));
 vec3 n=normalize(vec3(gd*edge*1.15,hh+.08));
 vec3 L=normalize(vec3(uL,1.));vec3 hv=normalize(L+vec3(0.,0.,1.));
 float df=max(dot(n,L),0.),nh=max(dot(n,hv),0.);
 float down=smoothstep(uY0,uY0+uZ,p.y);
 vec3 hot=vec3(1.,.19,.63),mid=vec3(.79,.07,.5),deep=vec3(.3,.015,.19);
 vec3 c=mix(hot,mid,smoothstep(0.,.45,t));c=mix(c,deep,smoothstep(.45,1.,t)*.85);
 c=mix(c,c*vec3(.62,.5,.7),down*.55);
 c*=.5+.75*df;
 c+=hot*pow(edge,5.)*.4*max(dot(gd,-normalize(uL)),0.);
 c+=vec3(.68,.6,.86)*smoothstep(.25,.7,-n.y*.75-n.x*.4)*hh*.22;
 c+=vec3(1.,.93,.98)*(pow(nh,140.)*2.2+pow(nh,26.)*.14);
 float a=clamp(.5-d/1.05,0.,1.);
 gl_FragColor=vec4(c*a,a);
}`;
    const sh = (t, s) => { const o = gl.createShader(t); gl.shaderSource(o, s); gl.compileShader(o); if (!gl.getShaderParameter(o, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(o)); return o; };
    let pr;
    try { pr = gl.createProgram(); gl.attachShader(pr, sh(gl.VERTEX_SHADER, VS)); gl.attachShader(pr, sh(gl.FRAGMENT_SHADER, FS)); gl.linkProgram(pr); if (!gl.getProgramParameter(pr, gl.LINK_STATUS)) throw new Error('link'); }
    catch (e) { console.warn('[DN melt]', e); return; }
    gl.useProgram(pr);
    gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer()); gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const al = gl.getAttribLocation(pr, 'a'); gl.enableVertexAttribArray(al); gl.vertexAttribPointer(al, 2, gl.FLOAT, false, 0, 0);
    const U = n => gl.getUniformLocation(pr, n);
    const uRes = U('uRes'), uN = U('uN'), uW = U('uW'), uK = U('uK'), uL = U('uL'), uA = U('uA'), uB = U('uB'), uC = U('uC'), uY0 = U('uY0'), uZ = U('uZ');
    gl.uniform1i(U('uLip'), 0);
    const tex = gl.createTexture(); gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    [gl.TEXTURE_MIN_FILTER, gl.TEXTURE_MAG_FILTER].forEach(k => gl.texParameteri(gl.TEXTURE_2D, k, gl.LINEAR));
    [gl.TEXTURE_WRAP_S, gl.TEXTURE_WRAP_T].forEach(k => gl.texParameteri(gl.TEXTURE_2D, k, gl.CLAMP_TO_EDGE));
    gl.enable(gl.BLEND); gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    title.appendChild(cv); title.classList.add('dn-melt-on');

    let seed = 11; const rnd = () => (seed = (seed * 16807) % 2147483647) / 2147483647;
    let G = null, drips = [], ready = false;
    const A = new Float32Array(MAXD * 4), B = new Float32Array(MAXD * 4), C = new Float32Array(MAXD * 4);
    const SK = Math.tan(10 * Math.PI / 180);
    /* exact euclidean distance (Felzenszwalb), squared, in place over rows then columns */
    const INF = 1e20;
    function edt1(f, n, v, z, o) {
      let k = 0; v[0] = 0; z[0] = -INF; z[1] = INF;
      for (let q = 1; q < n; q++) {
        let s = ((f[q] + q * q) - (f[v[k]] + v[k] * v[k])) / (2 * q - 2 * v[k]);
        while (s <= z[k]) { k--; s = ((f[q] + q * q) - (f[v[k]] + v[k] * v[k])) / (2 * q - 2 * v[k]); }
        k++; v[k] = q; z[k] = s; z[k + 1] = INF;
      }
      k = 0;
      for (let q = 0; q < n; q++) { while (z[k + 1] < q) k++; o[q] = (q - v[k]) * (q - v[k]) + f[v[k]]; }
    }
    function edt(grid, W, H) {
      const n = Math.max(W, H), f = new Float64Array(n), o = new Float64Array(n), v = new Int32Array(n), z = new Float64Array(n + 1);
      for (let x = 0; x < W; x++) { for (let y = 0; y < H; y++) f[y] = grid[y * W + x]; edt1(f, H, v, z, o); for (let y = 0; y < H; y++) grid[y * W + x] = o[y]; }
      for (let y = 0; y < H; y++) { for (let x = 0; x < W; x++) f[x] = grid[y * W + x]; edt1(f, W, v, z, o); for (let x = 0; x < W; x++) grid[y * W + x] = o[x]; }
    }

    /* 1. measure: draw the word exactly like the page (same font, spacing, skew from the line's bottom-left corner) */
    function measure() {
      const cs = getComputedStyle(line), fs = parseFloat(cs.fontSize) || 50, text = line.dataset.text;
      const dpr = Math.min(window.devicePixelRatio || 1, narrow.matches ? 1.75 : 2);
      const L0 = line.offsetLeft, T0 = line.offsetTop, Wl = line.offsetWidth, Hl = line.offsetHeight, Hb = T0 + Hl;
      const meta = title.nextElementSibling, tr = title.getBoundingClientRect(), blocks = [];
      if (meta) meta.querySelectorAll('.dn-sub, .dn-rating').forEach(el => { const rg = document.createRange(); rg.selectNodeContents(el); [...rg.getClientRects()].forEach(r => { if (r.width > 2) blocks.push([r.left - tr.left - 8, r.right - tr.left + 8, r.top - tr.top]); }); });
      const floorY = blocks.length ? Math.min(...blocks.map(b => b[2])) : Hb + fs;
      const pad = Math.ceil(fs * .3), ox = L0 - pad, oy = T0 + Hl * .3;
      const cw = Math.ceil(Wl + pad * 2 + Hl * SK + 12), ch = Math.ceil(Math.max(Hb + fs * .5, floorY + 2) - oy);
      cv.style.left = ox + 'px'; cv.style.top = oy + 'px'; cv.style.width = cw + 'px'; cv.style.height = ch + 'px';
      const W = Math.round(cw * dpr), H = Math.round(ch * dpr); cv.width = W; cv.height = H;
      const mc = document.createElement('canvas'); mc.width = W; mc.height = H;
      const g = mc.getContext('2d', { willReadFrequently: true });
      g.font = `900 ${fs}px ${cs.fontFamily}`;
      try { g.letterSpacing = cs.letterSpacing; } catch (e) {}
      g.textBaseline = 'alphabetic';
      const m = g.measureText(text), sx = m.width > 0 ? Wl / m.width : 1;
      const asc = m.fontBoundingBoxAscent || fs * .95, desc = m.fontBoundingBoxDescent || fs * .25;
      const lh = parseFloat(cs.lineHeight) || fs * .9, By = T0 + (lh - (asc + desc)) / 2 + asc;
      g.setTransform(sx * dpr, 0, -SK * dpr, dpr, (L0 + SK * (Hb - By) - ox) * dpr, (By - oy) * dpr);
      g.fillStyle = '#fff'; g.fillText(text, 0, 0);
      const im = g.getImageData(0, 0, W, H).data, by = (By - oy) * dpr;
      const bot = new Float32Array(W).fill(-1);
      for (let x = 0; x < W; x++) for (let y = Math.min(H - 1, Math.ceil(by + 3 * dpr)); y >= 0; y--) if (im[(y * W + x) * 4 + 3] > 120) { bot[x] = y; break; }
      const segs = []; let s0 = -1;
      for (let x = 0; x <= W; x++) {
        const on = x < W && bot[x] >= by - 1.6 * dpr;
        if (on && s0 < 0) s0 = x;
        if (!on && s0 >= 0) { if (x - s0 > 2 * dpr) segs.push([s0, x - 1]); s0 = -1; }
      }
      G = { W, H, dpr, fs, by, ox, oy, bot, segs, blocks, floorY, ex: null };
      gl.viewport(0, 0, W, H);
      gl.uniform2f(uRes, W, H); gl.uniform1f(uW, fs * .085 * dpr); gl.uniform1f(uK, fs * .055 * dpr);
      gl.uniform1f(uY0, by + 6 * dpr); gl.uniform1f(uZ, (floorY - oy) * dpr - by);
      pickDrips();
    }
    /* drips hang from the widest undersides, spread along the word; lengths fit the room above the line below */
    function pickDrips() {
      const { fs, dpr, segs, bot, blocks, floorY, ox, oy } = G;
      seed = 11;
      const cand = [];
      segs.forEach(([a, b]) => { const w = b - a, n = w > fs * .5 * dpr ? 2 : 1; for (let k = 0; k < n; k++) { const x = a + w * (n === 1 ? .4 + rnd() * .2 : (k ? .72 : .26) + rnd() * .06); cand.push({ x, w: w / n }); } });
      cand.sort((p, q) => q.w - p.w);
      const want = narrow.matches ? 4 : 6, pick = [];
      for (const c of cand) { if (pick.length >= want) break; if (pick.every(o => Math.abs(o.x - c.x) > fs * .55 * dpr)) pick.push(c); }
      pick.sort((p, q) => p.x - q.x);
      const room = x => { const xc = x / dpr + ox; let lim = floorY; blocks.forEach(([l, r, t]) => { if (xc > l && xc < r) lim = Math.min(lim, t); }); return (lim - 3 - oy) * dpr; };
      const shape = [1, .42, .78, .2, .92, .55];
      drips = pick.map((c, i) => {
        const rb = fs * (.036 + rnd() * .03) * dpr * (shape[i % shape.length] > .9 ? 1.18 : 1);
        return { x: c.x, w: c.w, rb, rBase: Math.min(c.w * .4, rb * 2.4), k: shape[i % shape.length] * (.85 + rnd() * .3), room: room(c.x),
          grow: 2.6 + rnd() * 2.2, delay: .15 + i * .24 + rnd() * .3, per: 3.8 + rnd() * 2.6, ph: rnd() * 3, feed: 0, y0: 0, Lmax: 0 };
      });
    }
    /* 2. the coat follows the extrusion (its direction moves with the pointer light) */
    function coat(exX, exY) {
      const { W, H, dpr, fs, by, bot, segs } = G;
      const ex = exX * dpr, ey = exY * dpr, K = 10;
      const eb = new Float32Array(W).fill(-1);
      for (let x = 0; x < W; x++) {
        let m = -1;
        for (let k = 0; k <= K; k++) { const xs = Math.round(x - ex * k / K); if (xs < 0 || xs >= W || bot[xs] < 0 || bot[xs] < by - 1.6 * dpr) continue; m = Math.max(m, bot[xs] + ey * k / K); }
        eb[x] = m;
      }
      const y0 = Math.max(0, Math.floor(by - fs * .25 * dpr)), y1 = Math.min(H, Math.ceil(by + ey + fs * .2 * dpr + 24 * dpr)), BH = y1 - y0;
      const ins = new Float64Array(W * BH), out = new Float64Array(W * BH);
      const skin = 1.6 * dpr, sag = fs * .028 * dpr;
      let run = 0;
      for (let x = 0; x < W; x++) {
        if (eb[x] < 0) { run = 0; for (let y = 0; y < BH; y++) { ins[y * W + x] = 0; out[y * W + x] = INF; } continue; }
        run++;
        const top = eb[x] - skin * 1.4, bt = eb[x] + skin + sag * (.5 + .5 * Math.sin(x * .11 + run * .07));
        for (let y = 0; y < BH; y++) { const yy = y + y0, on = yy >= top && yy <= bt; ins[y * W + x] = on ? INF : 0; out[y * W + x] = on ? 0 : INF; }
      }
      edt(ins, W, BH); edt(out, W, BH);
      const sd = new Uint8Array(W * H).fill(255);
      for (let y = 0; y < BH; y++) for (let x = 0; x < W; x++) { const i = y * W + x, d = out[i] > 0 ? Math.sqrt(out[i]) - .5 : -(Math.sqrt(ins[i]) - .5); sd[(y + y0) * W + x] = Math.max(0, Math.min(255, Math.round(128 + d * 4))); }
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.LUMINANCE, W, H, 0, gl.LUMINANCE, gl.UNSIGNED_BYTE, sd);
      drips.forEach(d => { const xi = Math.round(d.x); let y = -1; for (let k = -2; k <= 2; k++) y = Math.max(y, eb[Math.max(0, Math.min(W - 1, xi + k))]); d.y0 = (y < 0 ? by : y) + skin * .6; d.Lmax = Math.max(d.rb * 1.6, Math.min(d.room - d.y0 - d.rb * 2.2, fs * dpr * 1.1) * Math.min(1, d.k)); });
      G.ex = [exX, exY];
    }

    const easeOut = k => 1 - Math.pow(1 - Math.max(0, Math.min(1, k)), 3);
    let lx = -.4, ly = -.6, lxT = lx, lyT = ly, coatAt = 0;
    function step(t) {
      const ex = root.__dnEx || [5.3, 7.3];
      if (!G.ex || (Math.abs(ex[0] - G.ex[0]) + Math.abs(ex[1] - G.ex[1]) > .6 && performance.now() - coatAt > 90)) { coat(ex[0], ex[1]); coatAt = performance.now(); }
      let n = 0;
      for (const d of drips) {
        const k = (t - d.delay) / d.grow, gr = easeOut(k), run = Math.max(0, t - d.delay - d.grow * .6);
        let lumpY = 0, lumpR = 0;
        if (run > 0 && d.Lmax > d.rb * 3.5) {
          const c = (run + d.ph) / d.per, f = c % 1, L = d.Lmax * gr;
          if (f < .72) { const s = Math.pow(f / .72, 1.6); lumpY = L * s; lumpR = d.rb * (1.12 + .3 * Math.sin(Math.PI * s)); }
          d.feed = Math.min(1, Math.floor(c) * .16 + (f >= .72 ? .16 * easeOut((f - .72) / .28) : 0));
        }
        const L = d.Lmax * gr * (1 + .06 * d.feed);
        const i4 = n * 4;
        A[i4] = d.x; A[i4 + 1] = d.y0; A[i4 + 2] = Math.max(.5, L); A[i4 + 3] = d.rb * (.6 + .4 * gr);
        B[i4] = d.rBase * (.5 + .5 * gr); B[i4 + 1] = d.rb * (1.25 + .45 * gr + .35 * d.feed) * Math.min(1, .3 + gr); B[i4 + 2] = d.rb * (.78 + .1 * d.feed); B[i4 + 3] = Math.min(L, G.fs * .16 * G.dpr);
        C[i4] = 0; C[i4 + 1] = lumpY; C[i4 + 2] = lumpR * Math.min(1, gr * 1.4); C[i4 + 3] = 0;
        n++;
      }
      lx += (lxT - lx) * .08; ly += (lyT - ly) * .08;
      gl.uniform1f(uN, n); gl.uniform2f(uL, lx, ly);
      gl.uniform4fv(uA, A); gl.uniform4fv(uB, B); gl.uniform4fv(uC, C);
      gl.clearColor(0, 0, 0, 0); gl.clear(gl.COLOR_BUFFER_BIT);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    }

    let raf = 0, t0 = -1, vis = true;
    const run = now => {
      raf = 0;
      if (!ready) return;
      if (t0 < 0) t0 = now;
      step(reduced.matches ? 60 : (now - t0) / 1000);
      if (!reduced.matches && vis && !document.hidden) raf = requestAnimationFrame(run);
    };
    const kick = () => { if (!raf && ready && html.classList.contains('dn-revealed')) raf = requestAnimationFrame(run); };
    const rebuild = () => { try { measure(); G.ex = null; ready = true; kick(); } catch (e) { console.warn('[DN melt]', e); cv.remove(); title.classList.remove('dn-melt-on'); ready = false; } };
    (document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve()).then(rebuild);
    let rt = 0, lastW = 0;
    if ('ResizeObserver' in window) new ResizeObserver(() => { if (title.offsetWidth === lastW) return; lastW = title.offsetWidth; clearTimeout(rt); rt = setTimeout(rebuild, 150); }).observe(title);
    if ('IntersectionObserver' in window) new IntersectionObserver(e => { vis = e[0].isIntersecting; if (vis) kick(); }).observe(title);
    document.addEventListener('visibilitychange', kick);
    new MutationObserver(kick).observe(html, { attributes: true, attributeFilter: ['class'] });
    addEventListener('pointermove', e => {
      const r = title.getBoundingClientRect();
      if (r.bottom < -100 || r.top > innerHeight + 100) return;
      lxT = clamp((e.clientX - (r.left + r.width / 2)) / 420, -1, 1) * .7 - .2;
      lyT = clamp((e.clientY - (r.top + r.height / 2)) / 420, -1, 1) * .5 - .5;
      kick();
    }, { passive: true });
  }

  /* ── the live world ── */
  function world(root, api) {
    const host = root.querySelector('[data-dn-world]');
    const live = root.querySelector('[data-dn-live-label]');
    if (!host) return;
    if (narrow.matches && !/[?&]dnforce=1/.test(location.search)) { if (live) live.textContent = 'Deadly Nightshade'; host.classList.add('is-static'); return; }
    let gl2 = false;
    try { const c = document.createElement('canvas'); const g = c.getContext('webgl2'); gl2 = !!g; const x = g && g.getExtension('WEBGL_lose_context'); if (x) x.loseContext(); } catch (e) { gl2 = false; }
    if (!gl2) { if (live) live.textContent = 'Deadly Nightshade'; return; }
    const stage = root.querySelector('.dn-stage');
    const pdp = root.querySelector('.dn-pdp');
    const scenes = [...root.querySelectorAll('[data-dn-scene]')];
    const panel = root.querySelector('.dn-panel');
    const sticky = root.querySelector('.dn-stage__sticky');
    const frame = () => {
      const h = host.getBoundingClientRect();
      const r = (narrow.matches ? stage : sticky || stage).getBoundingClientRect();
      const visH = Math.max(120, Math.min(r.height, innerHeight - Math.max(r.top, 0)));
      const cy = (r.top - h.top) + visH * (narrow.matches ? .44 : .5);
      const cx = narrow.matches ? h.width / 2 : (r.left + r.width / 2) - h.left;
      return { x: clamp(cx / h.width, .2, .6), y: clamp(cy / h.height, .25, .65) };
    };
    let lastScene = 'hero';
    const rail = [...root.querySelectorAll('[data-dn-rail]')];
    rail.forEach(a => a.addEventListener('click', e => {
      const el = root.querySelector(a.getAttribute('href')); if (!el) return;
      e.preventDefault(); const r = el.getBoundingClientRect();
      window.scrollTo({ top: scrollY + r.top - innerHeight * .55 + r.height * .45, behavior: reduced.matches ? 'auto' : 'smooth' });
    }));
    const scene = () => {
      const line = innerHeight * .55;
      let name = 'hero', k = 0, prev = 'hero';
      scenes.forEach((s, i) => { const r = s.getBoundingClientRect(); if (r.top < line) { name = s.dataset.dnScene; prev = i ? scenes[i - 1].dataset.dnScene : 'hero'; k = clamp((line - r.top) / Math.max(1, r.height), 0, 1); } });
      if (name !== lastScene) { lastScene = name; root.classList.toggle('is-away', name !== 'hero'); root.dataset.dnAt = name; rail.forEach(a => a.classList.toggle('is-on', a.dataset.dnRail === name)); }
      return { name, k, prev };
    };
    const rvEl = root.querySelector('.dn-reviews');
    const paused = () => {
      if (root.classList.contains('is-photos')) return true;
      if (narrow.matches && panel) { const r = panel.getBoundingClientRect(); if (r.top < -40 && r.bottom > innerHeight) return true; }
      if (rvEl && rvEl.getBoundingClientRect().top < -320) return true;
      return false;
    };
    const small = narrow.matches || (Math.min(screen.width, screen.height) < 700);
    const opts = {
      three: root.dataset.three, world: root.dataset.world,
      label: small ? root.dataset.labelM : root.dataset.label, orm: root.dataset.orm, normal: root.dataset.normal,
      frame, scene, paused, input: stage,
      logo: root.dataset.logo || (() => { const i = document.querySelector('#pageheader .logo img, .logo img'); return i ? (i.currentSrc || i.src).replace(/_(\d+)x(\.\w+)/, '_720x$2') : ''; })()
    };
    const q = new URLSearchParams(location.search);
    if (q.get('dntm')) opts.tone = q.get('dntm');
    if (q.get('dnex')) opts.exposure = +q.get('dnex');
    let started = false;
    const start = () => {
      if (started) return; started = true;
      import(root.dataset.stageJs).then(m => m.createWorld(host, opts)).then(w => {
        api.stage = w; root.__dnStage = w;
        root.classList.add('is-world');
        api.flush();
      }).catch(err => { console.warn('[DN world]', err); if (live) live.textContent = 'Deadly Nightshade'; });
    };
    start();
    stage.addEventListener('pointerdown', () => root.classList.add('is-touched'), { once: true });
  }

  /* ── touch feel: press, ripple from the finger, a tick of haptics ── */
  const buzz = ms => { try { if (navigator.vibrate && matchMedia('(pointer: coarse)').matches) navigator.vibrate(ms); } catch (e) {} };
  function feel(root) {
    const SEL = '.dn-flav,.dn-pack,.dn-cta,.dn-shots button,.dn-more summary,.dn-acc__item summary,.dn-q summary,.dn-sticky__btn,.dn-ghost,.dn-rating';
    root.addEventListener('pointerdown', e => {
      const el = e.target.closest(SEL); if (!el || el.disabled || e.button > 0) return;
      el.classList.add('is-press');
      const up = () => { el.classList.remove('is-press'); removeEventListener('pointerup', up); removeEventListener('pointercancel', up); };
      addEventListener('pointerup', up); addEventListener('pointercancel', up);
      if (reduced.matches) return;
      const r = el.getBoundingClientRect(); let box = el.querySelector(':scope > .dn-rip');
      if (!box) { box = document.createElement('span'); box.className = 'dn-rip'; box.setAttribute('aria-hidden', 'true'); el.appendChild(box); }
      const i = document.createElement('i'); i.style.left = (e.clientX - r.left) + 'px'; i.style.top = (e.clientY - r.top) + 'px'; i.style.setProperty('--s', Math.max(r.width, r.height) / 9);
      box.appendChild(i); setTimeout(() => i.remove(), 700);
    }, { passive: true });
  }
  function lens(root) {
    const host = root.querySelector('[data-dn-world]'); if (!host || reduced.matches) return;
    host.classList.remove('is-lens'); void host.offsetWidth; host.classList.add('is-lens');
    clearTimeout(lens.t); lens.t = setTimeout(() => host.classList.remove('is-lens'), 520);
  }

  /* ── buy box: flavors, packages, focus, shots, cart ── */
  function buy(root, api) {
    const fmt = root.dataset.moneyFormat;
    const form = root.querySelector('.dn-form');
    if (!form) return;
    const idIn = form.querySelector('[data-dn-id]'), qtyIn = form.querySelector('[data-dn-qty]');
    const priceEl = form.querySelector('[data-dn-price]'), stockEl = form.querySelector('[data-dn-stock]');
    const flavName = form.querySelector('[data-dn-flavor-name]'), packName = form.querySelector('[data-dn-pack-name]');
    const flavs = [...form.querySelectorAll('.dn-flav')], packs = [...form.querySelectorAll('.dn-pack')];
    const btn = form.querySelector('[data-dn-add]'), label = btn.querySelector('[data-label]');
    const status = root.querySelector('[data-dn-status]');
    const sticky = root.querySelector('[data-dn-sticky]');
    const sName = root.querySelector('[data-dn-sticky-name]'), sPrice = root.querySelector('[data-dn-sticky-price]'), sBtn = root.querySelector('[data-dn-sticky-add]');
    const sSel = root.querySelector('[data-dn-sticky-flavor]'), sTab = root.querySelector('[data-dn-sticky-tab]'), sToggle = root.querySelector('[data-dn-sticky-toggle]'), sImg = sticky && sticky.querySelector('.dn-sticky__bar img');
    const dnArt = sImg ? sImg.getAttribute('src') : '';
    if (sSel) flavs.forEach((f, i) => { const op = document.createElement('option'); op.value = i; op.textContent = f.dataset.kind === 'dn' ? 'Deadly Nightshade' : f.dataset.name; op.disabled = f.dataset.available !== 'true'; sSel.appendChild(op); });
    const fView = root.querySelector('[data-dn-flavor-view]'), fImg = fView && fView.querySelector('img'), fTitle = root.querySelector('[data-dn-flavor-title]');
    const ctaBase = label.textContent.trim();
    const perEl = form.querySelector('[data-dn-per]'), wasEl = form.querySelector('[data-dn-was]'), servings = +root.dataset.servings || 20;
    const state = { flav: flavs.find(f => f.classList.contains('is-on')) || flavs[0], qty: 1 };

    function render() {
      const f = state.flav, cents = +f.dataset.cents, avail = f.dataset.available === 'true';
      const isDn = f.dataset.kind === 'dn';
      // Deadly Nightshade packs carry their bundle price (data-cents, honoured at checkout by the Shopify automatic discounts); other flavors stay qty x price
      const packCents = p => (isDn && +p.dataset.cents) || cents * (+p.dataset.qty || 1);
      const pack = packs.find(p => +p.dataset.qty === state.qty);
      const full = cents * state.qty, total = pack ? packCents(pack) : full;
      idIn.value = f.dataset.variant; qtyIn.value = state.qty;
      priceEl.textContent = money(total, fmt);
      if (wasEl) { wasEl.hidden = total >= full; wasEl.textContent = money(full, fmt); }
      if (perEl) { perEl.hidden = !isDn; if (isDn) perEl.textContent = `${money(Math.round(total / (servings * state.qty)), fmt)} / serving`; }
      packs.forEach(p => { const el = p.querySelector('[data-dn-pack-price]'); if (el) el.textContent = money(packCents(p), fmt); });
      flavName.textContent = f.dataset.name;
      if (stockEl) { const low = isDn ? +stockEl.dataset.low : 0; stockEl.classList.toggle('is-low', avail && low > 0); stockEl.lastChild.textContent = !avail ? (isDn ? 'Sold out — it will rise again' : `${f.dataset.name} — sold out`) : low > 0 ? `Only ${low} left in this drop` : 'In stock · ships in 1–2 days'; }
      btn.disabled = !avail; if (sBtn) sBtn.disabled = !avail;
      label.textContent = avail ? (state.qty > 1 ? `Add ${state.qty} to cart` : ctaBase) : 'Sold out';
      if (sName) sName.textContent = f.dataset.kind === 'dn' ? 'Deadly Nightshade' : `Symbiote · ${f.dataset.name}`;
      if (sPrice) sPrice.textContent = money(total, fmt) + (state.qty > 1 ? ` · ${state.qty} tubs` : (isDn ? ` · ${money(Math.round(cents / servings), fmt)}/serving` : ''));
      if (sTab) sTab.textContent = `${f.dataset.kind === 'dn' ? 'Deadly Nightshade' : f.dataset.name} · ${money(total, fmt)}`;
      if (sSel) sSel.value = String(flavs.indexOf(f));
      if (sImg) { const src = f.dataset.kind === 'dn' ? dnArt : (f.dataset.img || dnArt); if (sImg.getAttribute('src') !== src) sImg.setAttribute('src', src); }
    }
    function pickFlavor(f) {
      if (f.disabled) return;
      if (f.dataset.kind !== 'dn' && f.dataset.variant) { const a = root.querySelector('a[href*="host=classic"]'); let u = '/products/symbiote-pre-high-stim'; try { if (a) u = new URL(a.href, location.href).pathname; } catch (e) {} location.href = u + '?variant=' + f.dataset.variant; return; }
      state.flav = f;
      flavs.forEach(o => { const on = o === f; o.classList.toggle('is-on', on); o.setAttribute('aria-checked', on ? 'true' : 'false'); const em = o.querySelector('em'); if (o.dataset.kind === 'dn' && em) em.textContent = on ? 'Chosen — in stock' : 'Tap to bring it back'; });
      const core = f.dataset.kind !== 'dn';
      root.classList.toggle('is-flavor', core);
      const more = root.querySelector('[data-dn-more]'); if (core && more) more.open = true;
      if (fView) {
        if (core && f.dataset.img) { fImg.src = f.dataset.img; fTitle.textContent = f.dataset.name; }
        fView.classList.toggle('is-on', core && !!f.dataset.img);
      }
      api.call('sink', core); api.call('kick', core ? .1 : .18); buzz(8);
      render();
    }
    function pickPack(p) {
      state.qty = +p.dataset.qty || 1;
      packs.forEach(o => { const on = o === p; o.classList.toggle('is-on', on); o.setAttribute('aria-checked', on ? 'true' : 'false'); });
      packName.textContent = p.dataset.name;
      api.call('pack', state.qty); api.call('kick', .12); buzz(8);
      render();
    }
    const radios = (list, pick) => list.forEach((el, i) => {
      el.addEventListener('click', () => pick(el));
      el.addEventListener('keydown', e => {
        const k = e.key; if (!['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'].includes(k)) return;
        e.preventDefault(); const enabled = list.filter(x => !x.disabled); const at = enabled.indexOf(el);
        const nx = enabled[(at + (k === 'ArrowRight' || k === 'ArrowDown' ? 1 : -1) + enabled.length) % enabled.length]; nx.focus(); pick(nx);
      });
    });
    radios(flavs, pickFlavor); radios(packs, pickPack);

    /* label focus + camera shots */
    const focusBtns = [...root.querySelectorAll('.dn-stage__focus [data-focus]')];
    const shotBtns = [...root.querySelectorAll('[data-shot]')];
    const markShot = name => shotBtns.forEach(b => { const on = b.dataset.shot === name; b.classList.toggle('is-on', on); b.setAttribute('aria-pressed', on ? 'true' : 'false'); });
    const markFocus = name => focusBtns.forEach(b => b.classList.toggle('is-on', b.dataset.focus === name));
    focusBtns.forEach(b => b.addEventListener('click', () => {
      const n = b.dataset.focus; markFocus(n); markShot('hero');
      if (n === 'front') { api.call('shot', 'hero'); api.call('focus', 'front'); setTimeout(() => api.call('release'), 1400); }
      else api.call('focus', n);
    }));
    shotBtns.forEach(b => b.addEventListener('click', () => {
      const n = b.dataset.shot; markShot(n); markFocus(n === 'hero' ? 'front' : '');
      if (n === 'facts') { api.call('shot', 'hero'); api.call('focus', 'facts'); } /* the camera turns the tub to its facts panel */
      else api.call('shot', n);
      lens(root); buzz(6);
    }));
    root.querySelectorAll('.dn-acc__item').forEach(d => d.addEventListener('toggle', () => {
      if (narrow.matches) return;
      if (d.open) { markShot('hero'); api.call('focus', d.dataset.focus); }
      else if (!root.querySelector('.dn-acc__item[open]')) { markFocus('front'); api.call('release'); }
    }));

    /* cart */
    async function addToCart() {
      const res = await fetch(root.dataset.addUrl, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id: Number(idIn.value), quantity: state.qty }] }) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.description || data.message || 'Could not add to cart. Try again.');
      document.dispatchEvent(new CustomEvent('theme:cartchanged', { bubbles: true, cancelable: false }));
      if (window.theme && theme.settings && theme.settings.cart_type === 'drawer') document.dispatchEvent(new CustomEvent('theme:open-cart-drawer', { bubbles: true, cancelable: false }));
      return data;
    }
    btn.type = 'button';
    async function go(e, from) {
      if (e) { e.preventDefault(); e.stopPropagation(); }
      if (btn.disabled || btn.classList.contains('is-busy')) return;
      const b = from || btn; const bl = b === btn ? label : b; const before = bl.textContent;
      btn.classList.add('is-busy'); b.classList.add('is-busy'); bl.textContent = 'Bonding…'; status.textContent = '';
      try {
        await addToCart();
        api.call('flash'); api.call('pulse'); api.call('kick', .3); buzz([14, 40, 22]);
        b.classList.remove('is-busy'); btn.classList.remove('is-busy'); b.classList.add('is-done'); bl.textContent = b === btn ? 'Bonded — in your cart' : 'In your cart';
        status.innerHTML = `It's yours. <a href="${root.dataset.cartUrl}">View cart</a> · <a href="/checkout">Checkout</a>`;
        setTimeout(() => { b.classList.remove('is-done'); bl.textContent = before; render(); }, 2600);
      } catch (err) { b.classList.remove('is-busy'); btn.classList.remove('is-busy'); bl.textContent = before; status.textContent = err.message; }
    }
    btn.addEventListener('click', go);
    form.addEventListener('submit', go);
    if (sBtn) sBtn.addEventListener('click', e => go(e, sBtn));
    if (sSel) sSel.addEventListener('change', () => { const f = flavs[+sSel.value]; if (f) pickFlavor(f); });

    /* sticky buy bar once the main button scrolls away; it can be tucked away */
    if (sticky) {
      let ctaVis = true, on = false;
      let min = false; try { min = sessionStorage.getItem('nfdn-bar') === 'min'; } catch (e) {}
      const apply = () => {
        sticky.classList.toggle('is-on', on); sticky.classList.toggle('is-min', min);
        sticky.setAttribute('aria-hidden', on ? 'false' : 'true');
        const tab = on ? 0 : -1;
        [sBtn, sSel].forEach(el => { if (el) el.tabIndex = on && !min ? 0 : -1; });
        if (sToggle) { sToggle.tabIndex = tab; sToggle.setAttribute('aria-expanded', min ? 'false' : 'true'); sToggle.setAttribute('aria-label', min ? 'Show the buy bar' : 'Hide the buy bar'); }
      };
      const sync = () => { const r = btn.getBoundingClientRect(); const v = !ctaVis && (r.bottom < 0 || narrow.matches); if (v !== on) { on = v; apply(); } };
      new IntersectionObserver(es => { ctaVis = es[0].isIntersecting; sync(); }).observe(btn);
      addEventListener('scroll', () => { if (!ctaVis || on) sync(); }, { passive: true });
      if (sToggle) sToggle.addEventListener('click', () => { min = !min; try { sessionStorage.setItem('nfdn-bar', min ? 'min' : 'open'); } catch (e) {} apply(); });
      apply();
    }
    render();
  }

  /* ── photo vault (desktop): the product photos over the paused world ── */
  function vault(root, api) {
    const box = root.querySelector('[data-dn-vault]');
    const strip = root.querySelector('[data-dn-strip]');
    const toggle = root.querySelector('[data-dn-view]');
    if (!box || !strip || !toggle) return;
    const thumbs = [...strip.querySelectorAll('[data-i]')];
    if (!thumbs.length) return;
    const frame = box.querySelector('[data-dn-vault-frame]');
    const live = box.querySelector('[data-dn-vault-live]');
    const zoomBtn = box.querySelector('[data-dn-vault-zoom]');
    const items = thumbs.map(t => ({ src: t.dataset.src, srcset: t.dataset.srcset, cap: t.dataset.cap, key: t.dataset.key, cut: t.hasAttribute('data-cut'), alt: t.dataset.alt }));
    const n = items.length;
    const SIZES = 'min(46vw, 680px)';
    let cur = -1, open = false, zoomed = false, token = 0, layer = null;

    const preload = k => { const d = items[(k + n) % n]; if (d.pre) return; d.pre = 1; const im = new Image(); im.decoding = 'async'; im.sizes = SIZES; im.srcset = d.srcset; im.src = d.src; };
    const makeLayer = d => {
      const l = document.createElement('div'); l.className = 'dn-vault__layer' + (d.cut ? ' is-cut' : '');
      const im = document.createElement('img'); im.alt = d.alt; im.decoding = 'async'; im.draggable = false; im.sizes = SIZES; im.srcset = d.srcset; im.src = d.src;
      l.appendChild(im); return l;
    };
    const ready = im => Promise.race([im.decode ? im.decode().catch(() => {}) : Promise.resolve(), new Promise(r => setTimeout(r, 1400))]);

    const setZoom = (on, x = .5, y = .5) => {
      zoomed = on && !!layer; box.classList.toggle('is-zoom', zoomed);
      zoomBtn.setAttribute('aria-pressed', zoomed ? 'true' : 'false'); zoomBtn.setAttribute('aria-label', zoomed ? 'Zoom out' : 'Zoom in');
      const im = layer && layer.querySelector('img'); if (!im) return;
      if (zoomed) { im.style.transformOrigin = (x * 100).toFixed(2) + '% ' + (y * 100).toFixed(2) + '%'; im.sizes = Math.round(frame.clientWidth * 2.4) + 'px'; }
    };
    const point = e => { const r = frame.getBoundingClientRect(); return [clamp((e.clientX - r.left) / r.width, 0, 1), clamp((e.clientY - r.top) / r.height, 0, 1)]; };

    /* the symbiote swallows the photo and spits out the next one (same goo shader as the takeover) */
    const goo = (() => {
      let cv = null, ctx, raf = 0, px = 1, stop = null;
      const t0 = performance.now(), st = { c: 0, dir: 1, seed: [] }, B = new Float32Array(56);
      const fit = () => { if (!cv) return; px = Math.min(devicePixelRatio || 1, 2) * .8; cv.width = Math.max(2, Math.round(frame.clientWidth * px)); cv.height = Math.max(2, Math.round(frame.clientHeight * px)); };
      const init = () => {
        if (ctx !== undefined) return !!ctx;
        ctx = null;
        if (reduced.matches || !window.NFGoo || !NFGoo.gl) return false;
        cv = document.createElement('canvas'); cv.className = 'dn-vault__goo'; cv.setAttribute('aria-hidden', 'true'); frame.appendChild(cv);
        ctx = NFGoo.gl(cv); if (!ctx) { cv.remove(); cv = null; return false; }
        fit(); if ('ResizeObserver' in window) new ResizeObserver(fit).observe(frame);
        return true;
      };
      const seed = dir => { st.dir = dir; st.seed = [0, 1, 2].map(i => ({ y: .2 + i * .3 + (Math.random() - .5) * .12, dy: (Math.random() - .5) * .34, bend: (Math.random() - .5) * .24, r: .075 + Math.random() * .035, sp: .8 + Math.random() * .5 })); };
      const draw = now => {
        const { gl, u } = ctx, W = cv.width, H = cv.height, m = Math.min(W, H), c = st.c;
        cv.style.opacity = Math.min(1, c * 7).toFixed(3);
        gl.viewport(0, 0, W, H); gl.clearColor(0, 0, 0, 0); gl.clear(gl.COLOR_BUFFER_BIT);
        if (c <= .002) return;
        const sx = st.dir > 0 ? W + 8 : -8, ex = st.dir > 0 ? -m * .12 : W + m * .12;
        let n = 0;
        st.seed.forEach(sd => [[.18, 1.25], [.42, 1], [.66, .85], [.9, .7]].forEach(([f, rr]) => {
          const ff = f * Math.min(1, c * sd.sp * 1.1), y0 = sd.y * H, y1 = (sd.y + sd.dy) * H;
          B[n * 4] = sx + (ex - sx) * ff; B[n * 4 + 1] = y0 + (y1 - y0) * ff + Math.sin(ff * Math.PI) * sd.bend * m;
          B[n * 4 + 2] = m * sd.r * rr * (.45 + .55 * Math.min(1, c * 2)); B[n * 4 + 3] = 1; n++;
        }));
        gl.uniform2f(u.uRes, W, H); gl.uniform1f(u.uTime, (now - t0) / 1000);
        gl.uniform1f(u.uFlood, c * 1.15); gl.uniform1f(u.uFocusAmt, c * 1.32); gl.uniform2f(u.uFocus, st.dir > 0 ? W : 0, H * .5);
        gl.uniform1f(u.uN, n); gl.uniform1f(u.uPx, px); gl.uniform4fv(u.uB, B); gl.uniform1f(u.uRelief, 1);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
      };
      const to = (target, dur, ease) => new Promise(res => {
        if (stop) stop(); cancelAnimationFrame(raf);
        const from = st.c, s0 = performance.now(); let live = true;
        stop = () => { if (live) { live = false; res(false); } };
        const step = now => {
          if (!live) return;
          const k = Math.min(1, (now - s0) / dur); st.c = from + (target - from) * ease(k); draw(now);
          if (k < 1) raf = requestAnimationFrame(step); else { live = false; stop = null; res(true); }
        };
        raf = requestAnimationFrame(step);
      });
      return {
        ok: init,
        /* .66 already swallows the whole photo: the swap never shows a flat black card */
        eat(dir) { if (st.c < .05) seed(dir); return to(.66, 340, k => k * k * (3 - 2 * k)); },
        hold(dir) { seed(dir); st.c = .66; draw(performance.now()); },
        spit() { return to(0, 540, k => 1 - Math.pow(1 - k, 2.4)); }
      };
    })();

    async function show(k, dir = 1) {
      k = ((k % n) + n) % n; if (k === cur) return;
      const first = cur < 0; cur = k; const my = ++token; const d = items[k];
      if (zoomed) setZoom(false);
      thumbs.forEach((t, j) => { t.classList.toggle('is-on', j === k); if (j === k) t.setAttribute('aria-current', 'true'); else t.removeAttribute('aria-current'); });
      const t = thumbs[k], sr = strip.getBoundingClientRect(), tr = t.getBoundingClientRect();
      if (tr.left < sr.left + 6 || tr.right > sr.right - 6) strip.scrollTo({ left: strip.scrollLeft + (tr.left - sr.left) - (sr.width - tr.width) / 2, behavior: reduced.matches ? 'auto' : 'smooth' });
      live.textContent = 'Photo ' + (k + 1) + ' of ' + n + ': ' + d.cap;
      const next = makeLayer(d); next.style.visibility = 'hidden'; frame.insertBefore(next, frame.querySelector('.dn-vault__goo'));
      const sym = goo.ok();
      if (sym && first) goo.hold(dir);
      const im = next.querySelector('img');
      if (sym && !first) await Promise.all([goo.eat(dir), ready(im)]); else await ready(im);
      if (my !== token) { next.remove(); return; }
      const old = layer; layer = next; next.classList.add('is-cur'); next.style.visibility = '';
      preload(k + 1); preload(k - 1);
      if (sym) {
        /* swap under full cover, then the symbiote lets go */
        frame.querySelectorAll('.dn-vault__layer').forEach(l => { if (l !== next) l.remove(); });
        goo.spit();
        return;
      }
      if (first || reduced.matches || !old || !next.animate) { frame.querySelectorAll('.dn-vault__layer').forEach(l => { if (l !== next) l.remove(); }); return; }
      const fade = next.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 260, easing: 'ease-out' });
      const drop = () => { if (old.isConnected && old !== layer) old.remove(); };
      fade.finished.then(drop, drop);
    }

    const setOpen = on => {
      if (on === open) return; open = on;
      root.classList.toggle('is-photos', on);
      box.toggleAttribute('inert', !on); box.setAttribute('aria-hidden', on ? 'false' : 'true'); strip.toggleAttribute('inert', !on);
      toggle.setAttribute('aria-pressed', on ? 'true' : 'false'); toggle.setAttribute('aria-label', on ? 'Back to the 3D world' : 'Show the product photos');
      if (on) { if (cur < 0) show(0); lens(root); }
      else { setZoom(false); }
      if (root.__dnFit) root.__dnFit();
      buzz(6);
    };
    const warm = () => { preload(0); preload(1); };
    toggle.addEventListener('pointerenter', warm, { once: true });
    toggle.addEventListener('focus', warm, { once: true });
    toggle.addEventListener('click', () => setOpen(!open));
    thumbs.forEach((t, j) => t.addEventListener('click', () => show(j, j >= cur ? 1 : -1)));
    box.querySelectorAll('[data-dn-vault-go]').forEach(b => b.addEventListener('click', () => { const d = +b.dataset.dnVaultGo; show(cur + d, d); }));
    /* the world never sees a drag that starts in the vault */
    box.addEventListener('pointerdown', e => e.stopPropagation());
    strip.addEventListener('pointerdown', e => e.stopPropagation());
    frame.addEventListener('click', e => { const [x, y] = point(e); setZoom(!zoomed, x, y); });
    frame.addEventListener('pointermove', e => { if (!zoomed || !layer) return; const [x, y] = point(e); const im = layer.querySelector('img'); if (im) im.style.transformOrigin = (x * 100).toFixed(2) + '% ' + (y * 100).toFixed(2) + '%'; });
    zoomBtn.addEventListener('click', () => setZoom(!zoomed));
    document.addEventListener('keydown', e => {
      if (!open || e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey) return;
      if (document.querySelector('dialog[open]')) return;
      const t = e.target;
      if (t && t.closest && t.closest('input,select,textarea,[contenteditable],[role=radio],[role=radiogroup],.dn-panel,.dn-sticky')) return;
      const map = { ArrowRight: 1, ArrowLeft: -1 };
      if (map[e.key]) { e.preventDefault(); show(cur + map[e.key], map[e.key]); }
      else if (e.key === 'Home') { e.preventDefault(); show(0, -1); }
      else if (e.key === 'End') { e.preventDefault(); show(n - 1, 1); }
      else if (e.key === 'Escape') { e.preventDefault(); if (zoomed) setZoom(false); else { setOpen(false); toggle.focus({ preventScroll: true }); } }
    });
    /* reading the label in the buy box shows the matching photo */
    const pick = { facts: 'facts', mature: 'back', drop: 'drop' };
    root.querySelectorAll('.dn-acc__item').forEach(d => d.addEventListener('toggle', () => {
      if (!open || !d.open) return;
      const k = items.findIndex(x => x.key && x.key === pick[d.dataset.focus]); if (k >= 0) show(k, k >= cur ? 1 : -1);
    }));
    /* leaving the product hands the world back */
    const firstScene = root.querySelector('[data-dn-scene]');
    addEventListener('scroll', () => { if (open && firstScene && firstScene.getBoundingClientRect().top < innerHeight * .55) setOpen(false); }, { passive: true });
    narrow.addEventListener && narrow.addEventListener('change', () => { if (narrow.matches) setOpen(false); });
  }

  /* ── corner widgets (loyalty launcher, popup teaser): the dock and the buy bar step around them ── */
  function dodge(root) {
    const dock = root.querySelector('.dn-stage__dock');
    const bar = root.querySelector('[data-dn-sticky]');
    const SEL = '#btnIframe,[class*="kl-teaser"],body>iframe,body>div>iframe';
    let watched = new Set(), t = 0;
    const ro = 'ResizeObserver' in window ? new ResizeObserver(() => soon()) : null;
    const mo = 'MutationObserver' in window ? new MutationObserver(() => soon()) : null;
    /* widgets fade in/out by style on themselves or a wrapper: watch those, not the whole page */
    const watch = el => {
      if (watched.has(el)) return; watched.add(el);
      if (ro) ro.observe(el);
      for (let n = el, i = 0; n && n !== document.body && i < 3; n = n.parentElement, i++) if (mo) mo.observe(n, { attributes: true, attributeFilter: ['style', 'class', 'hidden'] });
    };
    const shown = el => { for (let n = el; n && n !== document.body; n = n.parentElement) { const cs = getComputedStyle(n); if (cs.display === 'none' || +cs.opacity < .05) return false; } return getComputedStyle(el).visibility !== 'hidden'; };
    const corners = () => {
      const H = innerHeight, W = innerWidth, out = { l: null, r: null };
      document.querySelectorAll(SEL).forEach(el => {
        if (root.contains(el)) return;
        const r = el.getBoundingClientRect();
        if (r.width < 8 || r.height < 8 || r.width > 340 || r.height > 220 || r.top < H * .55 || r.bottom < H - 160) return;
        watch(el);
        if (!shown(el)) return;
        if (r.left < W * .4) { if (!out.l || r.right > out.l.right) out.l = r; }
        else if (r.right > W * .6) { if (!out.r || r.left < out.r.left) out.r = r; }
      });
      return out;
    };
    const fit = () => {
      t = 0;
      ['--dn-dock-l', '--dn-sticky-w'].forEach(v => root.style.removeProperty(v));
      if (narrow.matches) return;
      const c = corners();
      /* the dock reaches the bottom edge near the top of the page: if the widget sits under its left end, the dock slides right of it */
      if (dock && c.l) {
        const rs = [...dock.children].filter(k => k.getClientRects().length).map(k => k.getBoundingClientRect());
        if (rs.length) {
          const left = Math.min(...rs.map(r => r.left)), dr = dock.getBoundingClientRect();
          if (c.l.right + 12 > left) root.style.setProperty('--dn-dock-l', Math.ceil(c.l.right + 16 - (dr.left - 12)) + 'px');
        }
      }
      if (bar && (c.l || c.r)) {
        const W = innerWidth, need = Math.max(c.l ? c.l.right + 14 : 0, c.r ? W - c.r.left + 14 : 0);
        const w = Math.min(780, W - 24, W - 2 * need);
        if (w < Math.min(780, W - 24)) root.style.setProperty('--dn-sticky-w', Math.max(540, Math.floor(w)) + 'px');
      }
    };
    const soon = () => { if (!t) t = setTimeout(fit, 180); };
    root.__dnFit = soon;
    addEventListener('resize', soon);
    if (mo) new MutationObserver(soon).observe(document.body, { childList: true, subtree: true });
    [600, 2500, 6000, 12000].forEach(ms => setTimeout(soon, ms));
    soon();
  }

  /* ── dialog + rise-in ── */
  function chrome(root) {
    root.querySelectorAll('[data-dn-open]').forEach(b => b.addEventListener('click', () => { const d = document.getElementById(b.dataset.dnOpen); if (d && d.showModal) d.showModal(); }));
    root.querySelectorAll('dialog').forEach(d => d.addEventListener('click', e => { if (e.target === d || e.target.closest('[data-dn-close]')) d.close(); }));
    const rise = root.querySelectorAll('.dn-story__body,.dn-ritual__head,.dn-rite__card,.dn-faq__head,.dn-faq__list,.dn-reviews__head');
    const count = el => { const to = +el.dataset.dnCount, fmt = n => n.toLocaleString('en-US'); if (reduced.matches || !to) return; const s0 = performance.now(), d = 1300; const step = now => { const k = Math.min(1, (now - s0) / d), e = 1 - Math.pow(1 - k, 4); el.textContent = fmt(Math.round(to * e)); if (k < 1) requestAnimationFrame(step); }; el.textContent = '0'; requestAnimationFrame(step); };
    if ('IntersectionObserver' in window) { const cio = new IntersectionObserver(es => es.forEach(e => { if (e.isIntersecting) { count(e.target); cio.unobserve(e.target); } }), { threshold: .6 }); root.querySelectorAll('[data-dn-count]').forEach(el => cio.observe(el)); }
    if (!reduced.matches && 'IntersectionObserver' in window) {
      const io = new IntersectionObserver(es => es.forEach(e => { if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); } }), { rootMargin: '0px 0px -8% 0px' });
      rise.forEach(el => { el.setAttribute('data-dn-rise', ''); io.observe(el); });
    }
    const header = document.querySelector('#pageheader, .section-header');
    const stageEl = root.querySelector('.dn-stage');
    const setH = () => {
      if (header) root.style.setProperty('--dn-header', Math.round(header.getBoundingClientRect().height) + 'px');
      if (stageEl) root.style.setProperty('--dn-stage-top', Math.round(clamp(stageEl.getBoundingClientRect().top + scrollY, 0, innerHeight * .4)) + 'px');
    };
    setH(); addEventListener('resize', setH);
  }

  /* ── reviews: the score as a number, the histogram open, the buy-box stars filled from the widget ── */
  function reviews(root) {
    const box = root.querySelector('.dn-reviews__box'); if (!box) return;
    const rating = root.querySelector('[data-dn-rating]');
    let tries = 0;
    const tick = () => {
      const w = box.querySelector('.jdgm-rev-widg');
      if (!w || !w.querySelector('.jdgm-rev-widg__header')) { if (++tries < 60) setTimeout(tick, 250); return; }
      const avg = parseFloat(w.dataset.averageRating || '0'), n = parseInt(w.dataset.numberOfReviews || '0', 10);
      const head = w.querySelector('.jdgm-rev-widg__header');
      if (avg > 0 && !head.querySelector('.dn-rv-big')) head.insertAdjacentHTML('afterbegin', `<p class="dn-rv-big">${avg.toFixed(1)}<small>/ 5</small></p>`);
      w.querySelectorAll('.jdgm-histogram.jdgm-temp-hidden').forEach(h => h.classList.remove('jdgm-temp-hidden'));
      if (rating && n > 0 && rating.classList.contains('is-empty')) {
        rating.classList.remove('is-empty');
        const a = rating.querySelector('[data-dn-rating-avg]'), c = rating.querySelector('[data-dn-rating-count]'), st = rating.querySelector('.dn-stars');
        if (a) a.textContent = avg.toFixed(1); if (c) c.textContent = n; if (st) st.style.setProperty('--r', avg);
      }
      if (!n) root.querySelector('.dn-reviews').classList.add('is-empty');
      const tidy = () => w.querySelectorAll('.jdgm-rev__reply,.jdgm-rev__pics,.jdgm-rev__vids,.jdgm-rev__custom-form,.jdgm-rev__title,.jdgm-rev__actions').forEach(el => { const empty = !el.textContent.trim() && !el.querySelector('img,video,iframe'); el.classList.toggle('dn-x', empty); });
      tidy(); new MutationObserver(tidy).observe(w, { childList: true, subtree: true });
    };
    tick();
    if (rating) rating.addEventListener('click', e => { const t = root.querySelector(rating.getAttribute('href')); if (!t) return; e.preventDefault(); window.scrollTo({ top: scrollY + t.getBoundingClientRect().top - 70, behavior: reduced.matches ? 'auto' : 'smooth' }); });
  }

  function init(root) {
    if (root.dataset.dnReady) return;
    root.dataset.dnReady = '1';
    const queue = [];
    const api = {
      stage: null,
      call(fn, ...a) { if (api.stage && api.stage[fn]) api.stage[fn](...a); else if (fn !== 'flash' && fn !== 'release') { for (let i = queue.length - 1; i >= 0; i--) if (queue[i][0] === fn) queue.splice(i, 1); queue.push([fn, a]); } },
      flush() { queue.splice(0).forEach(([fn, a]) => api.stage[fn] && api.stage[fn](...a)); }
    };
    titleLight(root);
    titleMelt(root);
    feel(root);
    buy(root, api);
    vault(root, api);
    dodge(root);
    chrome(root);
    reviews(root);
    world(root, api);
  }

  function boot() {
    document.querySelectorAll('[data-dn]').forEach(init);
    if (window.NFGoo) NFGoo.arrive({}); else html.classList.add('dn-revealed');
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', e => { const r = e.target.querySelector('[data-dn]'); if (r) init(r); html.classList.add('dn-revealed'); });
})();

/* v9 — "Feed the host": transparent packshots, no white hover plates */
(function () {
  var SWAP = [
    [/\/files\/1_1\.webp\?v=\d+/g, '/files/carnage-pump-terror-tart-transparent.png?v=1792519573'],
    [/\/files\/2_3\.webp\?v=\d+/g, '/files/carnage-pump-terror-tart-back-transparent.png?v=1792519790']
  ];
  var ATTRS = ['src', 'srcset', 'data-src', 'data-srcset'];
  function swap(img) {
    ATTRS.forEach(function (a) {
      var v = img.getAttribute(a); if (!v) return;
      var n = v; SWAP.forEach(function (s) { n = n.replace(s[0], s[1]); });
      if (n !== v) img.setAttribute(a, n);
    });
  }
  function firstUrl(img) {
    var s = img.getAttribute('data-srcset') || img.getAttribute('srcset') || '', best = '', bw = 1e9;
    s.split(',').forEach(function (e) { var p = e.trim().split(/\s+/), w = parseInt(p[1], 10) || 9999; if (p[0] && w < bw) { bw = w; best = p[0]; } });
    var u = best || img.getAttribute('data-src') || img.getAttribute('src') || '';
    if (!u || u.indexOf('data:') === 0) return '';
    return u.indexOf('//') === 0 ? location.protocol + u : u;
  }
  function plate(img) {
    if (img.__nfChecked) return;
    var cont = img.closest('.image-cont'), u = firstUrl(img);
    if (!cont || !u) return;
    img.__nfChecked = 1;
    var p = new Image();
    p.onload = function () {
      try {
        var c = document.createElement('canvas'); c.width = 20; c.height = 25;
        var g = c.getContext('2d', { willReadFrequently: true }); g.drawImage(p, 0, 0, 20, 25);
        var d = g.getImageData(0, 0, 20, 25).data, white = 0;
        [[0, 0], [19, 0], [0, 24], [19, 24]].forEach(function (q) {
          var i = (q[1] * 20 + q[0]) * 4;
          if (d[i + 3] > 240 && 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2] > 200) white++;
        });
        if (white >= 3) cont.classList.add('nf-noswap');
      } catch (e) {}
    };
    p.src = u;
  }
  function scan(root) {
    root.querySelectorAll('img').forEach(swap);
    root.querySelectorAll('.product-block__image--show-on-hover img').forEach(plate);
  }
  function boot() {
    if (!document.body.classList.contains('nf-dn-world')) return;
    var sec = document.querySelector('.section-product-recommendations');
    if (!sec) return;
    scan(sec);
    new MutationObserver(function (ms) {
      var again = false;
      ms.forEach(function (m) { if (m.type === 'attributes') swap(m.target); else if (m.addedNodes.length) again = true; });
      if (again) scan(sec);
    }).observe(sec, { childList: true, subtree: true, attributes: true, attributeFilter: ATTRS });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();
